import React from "react"
import { Box } from "@mui/material"

import { dsColorNew } from "@palette"

/**
 * Iris's mark, ported from the IA workspace (`rentacar-ia-workspace/src/brand/IrisMark.jsx`):
 * a CLOSED iris ring around a large off-centre pupil on a white disc, catchlight top-left.
 * The geometry is the workspace's, untouched; only the colour changes — the gradient runs
 * from the app's web blue to its cyan accent, so the mark matches the restyled shell instead
 * of the workspace's chart blue. The disc stays white on purpose: invisible on the white
 * header button, a bright coin on the navy table heads.
 *
 * Three sizes, named rather than sprinkled: 16 (inline, beside text), 20 (the header
 * button), 24 (an avatar beside a message). Below 16 the catchlight would become a dirty
 * notch and the pupil would fuse with the ring, which is why the workspace keeps a reduced
 * variant; none of our slots go that small, but the switch is kept so a future one is safe.
 *
 * The static copy the chat widget loads by URL lives in `public/assets/iris.svg` and must
 * stay the same drawing and the same two palette values.
 */
export const IRIS = {
	inline: 16,
	button: 20,
	avatar: 24,
}

export default function IrisMark({ size = IRIS.avatar, sx = {} }) {
	// two marks on one page must not share a gradient id
	const uid = React.useId().replace(/[^a-zA-Z0-9]/g, '')
	const gradientId = `${uid}-iris`
	const detailed = size >= IRIS.inline

	return <Box sx={{ width: size, height: size, flexShrink: 0, display: 'flex', ...sx }}>
		<Box component="svg" viewBox="0 0 24 24" role="img" aria-label="Iris" sx={{ width: '100%', height: '100%' }}>
			<defs>
				<linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
					<stop offset="0%" stopColor={dsColorNew.cyan}/>
					<stop offset="100%" stopColor={dsColorNew.webBlue500}/>
				</linearGradient>
			</defs>
			{/* The disc is WHITE: invisible on light surfaces, a coin on dark ones. */}
			<circle cx="12" cy="12" r="12" fill={dsColorNew.white}/>
			{/* Closed ring, constant width */}
			<circle cx="12" cy="12" r="8.6" fill="none" stroke={`url(#${gradientId})`} strokeWidth="2.3"/>
			{/* The pupil nearly fills the ring, leaving a thin white gap; it shrinks below 16px so the gap survives */}
			<circle cx="12.2" cy="12.5" r={detailed ? 6.3 : 5.4} fill={`url(#${gradientId})`}/>
			{/* Catchlight top-left, where the gradient is light, so highlight and shading agree on one light source */}
			{detailed && <circle cx="9.6" cy="9.9" r="2.4" fill={dsColorNew.white}/>}
		</Box>
	</Box>
}
